import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useDispatch } from "react-redux";
import { useNavigate,
  useParams,
  NavLink } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Input,
  Select,
  Switch,
  Upload,
  Button,
  message,
  Typography,
  Space,
  Skeleton,
} from "antd";
import {
  PlusOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
  EyeInvisibleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  addArticle,
  updateArticle,
  getArticleById,
  uploadArticleImage,
} from "../../../api/article/article";
import { makeQuillModules, quillFormats } from "../../../components/common/quillConfig";
import PageHeader from "../../../components/PageLayout/PageHeader";
import { ARTICLE_CATEGORIES } from "./articleConstants";
import { resolveImageUrl } from "../../../utils/image";
import { semantic, neutral } from "../../../theme/tokens";
import StatusTag from "../../../components/cms/StatusTag";

const { Text } = Typography;
const { TextArea } = Input;

const PAGE_BG = "#F5F6FA";

export default function ArticleFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const quillRef = useRef(null);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(undefined);
  const [summary, setSummary] = useState("");
  const [content, setContent] = useState("");
  const [status, setStatus] = useState(true);
  const [fileList, setFileList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);

  // Nạp dữ liệu khi chỉnh sửa
  useEffect(() => {
    if (!isEdit) return;
    let active = true;
    (async () => {
      try {
        const data = await dispatch(getArticleById(id)).unwrap();
        if (!active) return;
        setTitle(data.title || "");
        setCategory(data.category || undefined);
        setSummary(data.summary || "");
        setContent(data.content || "");
        setStatus(data.status === 1 || data.status === true);
        setFileList(
          data.image
            ? [{ uid: "-1", name: "cover.png", status: "done", url: resolveImageUrl(data.image) }]
            : []
        );
      } catch (e) {
        message.error("Không tải được bài viết");
        navigate("/articles");
      } finally {
        if (active) setFetching(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id, isEdit, dispatch, navigate]);

  // Upload ảnh nội dung lên server rồi nhúng URL (thay vì base64 inline)
  const imageHandler = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size / 1024 / 1024 >= 2) {
        message.error("Ảnh nội dung phải nhỏ hơn 2MB!");
        return;
      }
      const editor = quillRef.current?.getEditor();
      const range = editor?.getSelection(true);
      try {
        const res = await dispatch(uploadArticleImage(file)).unwrap();
        const url = res?.url;
        if (url && editor) {
          const index = range ? range.index : 0;
          editor.insertEmbed(index, "image", url);
          editor.setSelection(index + 1);
        } else {
          message.error("Không nhận được đường dẫn ảnh từ server");
        }
      } catch (err) {
        message.error("Tải ảnh nội dung thất bại");
      }
    };
  };

  const quillModules = useMemo(() => makeQuillModules(imageHandler), []); // eslint-disable-line react-hooks/exhaustive-deps

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) message.error("Bạn chỉ có thể tải lên file hình ảnh!");
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) message.error("Hình ảnh phải nhỏ hơn 2MB!");
    return false; // chặn auto-upload, giữ file để submit qua FormData
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      message.error("Vui lòng nhập tiêu đề bài viết");
      return;
    }
    if (!content || content === "<p><br></p>") {
      message.error("Vui lòng nhập nội dung bài viết");
      return;
    }
    if (!isEdit && fileList.length === 0) {
      message.error("Vui lòng tải lên ảnh đại diện");
      return;
    }

    const formData = new FormData();
    formData.append("title", title.trim());
    formData.append("content", content);
    formData.append("status", status ? 1 : 0);
    if (category) formData.append("category", category);
    if (summary) formData.append("summary", summary);
    if (fileList.length > 0 && fileList[0].originFileObj) {
      formData.append("image", fileList[0].originFileObj);
    }

    try {
      setLoading(true);
      if (isEdit) {
        await dispatch(updateArticle({ id, data: formData })).unwrap();
        message.success("Cập nhật bài viết thành công");
      } else {
        await dispatch(addArticle(formData)).unwrap();
        message.success("Thêm bài viết thành công");
      }
      navigate("/articles");
    } catch (error) {
      message.error(isEdit ? "Không thể cập nhật bài viết" : "Không thể thêm bài viết");
    } finally {
      setLoading(false);
    }
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Tải lên</div>
    </div>
  );

  return (
    <div style={{ background: PAGE_BG, minHeight: "100%" }}>
      <PageHeader
        title={isEdit ? "Chỉnh sửa bài viết" : "Thêm bài viết mới"}
        subtitle="Soạn nội dung, phân loại và xuất bản tin tức hiển thị trên mini app"
        breadcrumb={[
          { title: <NavLink to="/articles">Quản lý bài viết</NavLink> },
          { title: isEdit ? "Chỉnh sửa" : "Thêm mới" },
        ]}
        extra={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/articles")}>
              Hủy
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={loading}
              onClick={handleSubmit}
            >
              {isEdit ? "Cập nhật" : "Lưu bài viết"}
            </Button>
          </Space>
        }
      />

      <div style={{ padding: 24 }}>
        {fetching ? (
          <Card>
            <Skeleton active paragraph={{ rows: 12 }} />
          </Card>
        ) : (
          <Row gutter={[24, 24]}>
            {/* ── Cột soạn thảo ── */}
            <Col xs={24} lg={16}>
              <Card bodyStyle={{ padding: 0 }}>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Tiêu đề bài viết"
                  bordered={false}
                  style={{
                    fontSize: 24,
                    fontWeight: 700,
                    padding: "20px 24px",
                  }}
                />
                <div style={{ borderTop: "1px solid #F1F5F9" }}>
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={content}
                    onChange={setContent}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Bắt đầu viết nội dung bài viết..."
                    style={{ height: 560, marginBottom: 56 }}
                  />
                </div>
              </Card>
            </Col>

            {/* ── Sidebar cấu hình (sticky) ── */}
            <Col xs={24} lg={8}>
              <div style={{ position: "sticky", top: 16, display: "flex", flexDirection: "column", gap: 16 }}>
                <Card size="small" title="Trạng thái xuất bản">
                  <Space align="center" style={{ justifyContent: "space-between", width: "100%" }}>
                    <Space>
                      {status ? (
                        <CheckCircleOutlined style={{ color: semantic.success }} />
                      ) : (
                        <EyeInvisibleOutlined style={{ color: neutral[400] }} />
                      )}
                      <Text strong>{status ? "Đã xuất bản" : "Bản nháp"}</Text>
                    </Space>
                    <Switch
                      checked={status}
                      onChange={setStatus}
                      checkedChildren="Bật"
                      unCheckedChildren="Tắt"
                    />
                  </Space>
                  <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
                    {status
                      ? "Bài viết sẽ hiển thị trên mini app ngay khi lưu."
                      : "Bài viết được lưu nhưng chưa hiển thị cho người dùng."}
                  </Text>
                </Card>

                <Card size="small" title="Phân loại & tóm tắt">
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Danh mục</Text>
                    <Select
                      allowClear
                      value={category}
                      onChange={setCategory}
                      placeholder="Chọn danh mục"
                      options={ARTICLE_CATEGORIES}
                      style={{ width: "100%", marginTop: 6 }}
                    />
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Tóm tắt</Text>
                    <TextArea
                      value={summary}
                      onChange={(e) => setSummary(e.target.value)}
                      rows={3}
                      maxLength={500}
                      showCount
                      placeholder="Tóm tắt ngắn hiển thị ở danh sách / mini app"
                      style={{ marginTop: 6 }}
                    />
                  </div>
                </Card>

                <Card size="small" title={<Space>Ảnh đại diện {!isEdit && <StatusTag tone="danger">Bắt buộc</StatusTag>}</Space>}>
                  <Upload
                    listType="picture-card"
                    fileList={fileList}
                    beforeUpload={beforeUpload}
                    onChange={({ fileList: fl }) => setFileList(fl)}
                    maxCount={1}
                  >
                    {fileList.length >= 1 ? null : uploadButton}
                  </Upload>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Định dạng ảnh, dung lượng &lt; 2MB. Hiển thị làm ảnh bìa bài viết.
                  </Text>
                </Card>
              </div>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
}
